import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getTagColor, getTagGradient, getTagEmoji, timeAgo } from '../../utils/communityHelpers';

/**
 * UpdatesTab Component
 * Display pinned and regular community posts with full native app parity
 */
const UpdatesTab = ({ community, posts, pinnedPosts, isUserMember }) => {
  const navigate = useNavigate();
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [isInIframe, setIsInIframe] = useState(false);
  const scrollRef = useRef(null);
  
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);
  
  const handlePostClick = (postId) => {
    navigate(`/group/${community.id}/post/${postId}`);
  };
  
  const scrollPinned = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400; // Scroll by one card width
      const currentScroll = scrollRef.current.scrollLeft;
      scrollRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  
  const toggleExpanded = (postId, e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedPosts);
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
    }
    setExpandedPosts(newExpanded);
  };
  
  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  // Empty state
  if (!posts || (posts.length === 0 && (!pinnedPosts || pinnedPosts.length === 0))) {
    return (
      <div className="empty-state">
        No posts yet in this community
      </div>
    );
  }
  
  const renderPostCard = (post, isPinned = false) => {
    const gradientColors = getTagGradient(post.tag);
    const tagEmoji = getTagEmoji(post.tag);
    const isExpanded = expandedPosts.has(post.id);
    const shouldShowSeeMore = post.content && post.content.length > 150;
    const displayContent = isExpanded ? post.content : truncateText(post.content);
    
    // Simplified pinned card design matching native app
    if (isPinned) {
      const getBadgeColor = (tag) => {
        switch (tag) {
          case 'Announcement': return '#667eea';
          case 'Mazal Tov': return '#56ab2f';
          case 'Update': return '#4facfe';
          case 'News': return '#ff9a9e';
          case 'Chesed': return '#a8edea';
          case 'Tehilim': return '#fcb69f';
          default: return '#9bb5ff';
        }
      };
      
      return (
        <div 
          key={post.id} 
          className="pinned-post-card"
          onClick={() => handlePostClick(post.id)}
        >
          <h3 className="pinned-post-headline">
            {post.title || truncateText(post.content, 60)}
          </h3>
          
          <div className="pinned-post-badges">
            <span 
              className="pinned-post-badge" 
              style={{ backgroundColor: getBadgeColor(post.tag) }}
            >
              {post.tag}
            </span>
          </div>
          
          <p className="pinned-post-body">
            {post.title ? truncateText(post.content, 120) : truncateText(post.content, 100)}
          </p>
        </div>
      );
    }
    
    // Regular post card (unchanged)
    return (
      <div 
        key={post.id} 
        className="post-card"
        onClick={() => handlePostClick(post.id)}
      >
        {/* Header Section */}
        <div className="post-card-header">
          {isPinned && <span className="post-pin-badge">📌</span>}
          <div className="post-community-logo-container">
            {community.logo_url ? (
              <img 
                src={community.logo_url} 
                alt={community.name}
                className="post-community-logo"
              />
            ) : (
              <div className="post-community-logo-fallback">
                {community.name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
            )}
          </div>
          <div className="post-header-text">
            <div className="post-community-name">{community.name}</div>
            <div className="post-meta">{timeAgo(post.created_at)}</div>
          </div>
        </div>
        
        {/* Image/Visual Section */}
        <div className="post-image-section">
          {post.image_url ? (
            <>
              <img 
                src={post.image_url}
                alt={post.title || 'Post image'}
                className="post-image"
              />
              {post.tag && (
                <div className="post-tag-overlay">
                  {post.tag.toUpperCase()}
                </div>
              )}
            </>
          ) : (
            <div 
              className="post-gradient-background"
              style={{
                background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`
              }}
            >
              <div className="post-gradient-text-overlay">
                <span className="post-gradient-emoji">{tagEmoji}</span>
                <span className="post-gradient-tag">{post.tag}</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Content Section */}
        <div className="post-content-section">
          {post.title && (
            <div className="post-title">{post.title}</div>
          )}
          
          {post.content && (
            <div className="post-content-text">{displayContent}</div>
          )}
          
          {shouldShowSeeMore && (
            <button 
              className="post-see-more-btn"
              onClick={(e) => toggleExpanded(post.id, e)}
            >
              {isExpanded ? 'See Less' : 'See More'}
            </button>
          )}
          
          {post.image_caption && (
            <div className="post-image-caption">{post.image_caption}</div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="updates-container">
      {/* Pinned Posts */}
      {pinnedPosts && pinnedPosts.length > 0 && (
        <div className="pinned-posts-section">
          <div className="pinned-section-header">
            <h2 className="pinned-section-title">Pinned</h2>
            {isInIframe && pinnedPosts.length > 1 && (
              <div className="pinned-scroll-arrows" style={{ display: 'flex' }}>
                <button 
                  className="scroll-arrow scroll-left"
                  onClick={() => scrollPinned('left')}
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={18} />
                </button>
                <button 
                  className="scroll-arrow scroll-right"
                  onClick={() => scrollPinned('right')}
                  aria-label="Scroll right"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
          <div 
            className="pinned-posts-scroll" 
            ref={scrollRef}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {pinnedPosts.map(post => renderPostCard(post, true))}
          </div>
        </div>
      )}
      
      {/* Section Header if we have both pinned and regular posts */}
      {pinnedPosts && pinnedPosts.length > 0 && posts && posts.length > 0 && (
        <div className="updates-section-header-row">
          <div className="updates-section-title">All Updates</div>
          {pinnedPosts.length > 1 && (
            <div className="pinned-scroll-arrows">
              <button 
                className="scroll-arrow scroll-left"
                onClick={() => scrollPinned('left')}
                aria-label="Scroll left"
              >
                ‹
              </button>
              <button 
                className="scroll-arrow scroll-right"
                onClick={() => scrollPinned('right')}
                aria-label="Scroll right"
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Regular Posts */}
      {posts && posts.length > 0 && (
        <div className="regular-posts-section">
          {posts.map(post => renderPostCard(post, false))}
        </div>
      )}
    </div>
  );
};

export default UpdatesTab;
